"use client"

import { memo, useCallback, useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { useQuery } from "@tanstack/react-query"
import { ArrowLeft } from "lucide-react"

import { getKeywordSuggestions } from "@/lib/client/api/search"
import { cn } from "@/lib/utils"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { SearchInput } from "@/features/layout/headers/global-search"
import { useRecentlySearchedStore } from "@/features/layout/headers/recently-searched-store"
import { Button } from "@/components/ui/button"

import {
  BackdropOverlay,
  KeywordSuggestions,
  SearchResultsDropdown,
} from "./mobile-search-components"

export function HomeMobileSearch({
  hotKeywords,
}: {
  hotKeywords: React.ReactNode
}) {
  // State
  const [open, setOpen] = useState(false)
  const [keyword, setKeyword] = useState("")
  const [debouncedKeyword] = useDebouncedValue(keyword, 500)

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Hooks
  const router = useRouter()
  const addKeyword = useRecentlySearchedStore((state) => state.addKeyword)

  // Query
  const { data: suggestions } = useQuery({
    queryKey: ["keyword-suggestions", debouncedKeyword],
    queryFn: () => getKeywordSuggestions(debouncedKeyword),
    enabled: !!debouncedKeyword && open,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  })

  // Handle search
  const handleSearch = useCallback(
    (searchKeyword: string, type: string = "dish") => {
      if (searchKeyword.trim()) {
        router.push(
          `/search?keyword=${encodeURIComponent(searchKeyword)}&type=${type}`
        )
        addKeyword(searchKeyword)
        setOpen(false)
      }
    },
    [router, addKeyword]
  )

  // Outside click handler
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      searchInputRef.current &&
      !searchInputRef.current.contains(event.target as Node)
    ) {
      setOpen(false)
    }
  }, [])

  // Close search on escape key
  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setOpen(false)
    }
  }, [])

  // Add/remove event listeners
  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside)
      document.addEventListener("keydown", handleEscapeKey)
    } else {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscapeKey)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscapeKey)
    }
  }, [open, handleClickOutside, handleEscapeKey])

  return (
    <div
      className={cn(
        "transition-all duration-300 ease-search-button w-full flex-col",
        open && "fixed inset-0 z-50 bg-white pt-4"
      )}
      data-testid="mobile-search-wrapper"
    >
      <div role="search" className={cn("flex-1", open && "z-40 bg-white px-3")}>
        <label htmlFor="mobile-search" className="sr-only">
          Tìm món trên beFood
        </label>

        <div className="flex items-center gap-2">
          {open && (
            <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
              <ArrowLeft />
            </Button>
          )}

          <div className="flex-1">
            <SearchInput
              keyword={keyword}
              setKeyword={setKeyword}
              onFocus={() => setOpen(true)}
              onSearch={handleSearch}
            />
          </div>
        </div>
      </div>

      {open && (
        <SearchResultsDropdown
          open={open}
          keyword={keyword}
          suggestions={suggestions}
          onSearch={handleSearch}
          hotKeywords={hotKeywords}
          dropdownRef={dropdownRef}
        />
      )}
    </div>
  )
}
